/**
 * The newsletters' record — the vocabulary and limits shared by the
 * `email_campaign_tracker` saga, the email-campaign canonical actions and
 * the `/campaigns` page (email-campaigns, 2026-10-17;
 * build-output/architecture/email-campaigns.md). They live in this one
 * package so a form, a JSON Schema, the saga and migration 232's CHECKs all
 * say the same thing. Pure consts, no deps: the page bundles this file
 * through a Vite alias, the same way it bundles `whatsapp-newsletter`.
 */

/**
 * The five checkpoints after a send, in order (migration 232's CHECK on
 * `email_campaign_checkpoints.checkpoint`). `hours` is the offset from the
 * send; the as-of hour is the first hour boundary at or after it.
 */
export const EMAIL_CAMPAIGN_CHECKPOINTS = [
  { key: "6h", hours: 6 },
  { key: "24h", hours: 24 },
  { key: "48h", hours: 48 },
  { key: "7d", hours: 168 },
  { key: "14d", hours: 336 },
] as const;
export type EmailCampaignCheckpointKey = (typeof EMAIL_CAMPAIGN_CHECKPOINTS)[number]["key"];
export const EMAIL_CAMPAIGN_CHECKPOINT_KEYS: ReadonlyArray<EmailCampaignCheckpointKey> =
  EMAIL_CAMPAIGN_CHECKPOINTS.map((c) => c.key);

/**
 * How long the tracker waits past a checkpoint's as-of hour before it asks
 * Resend: Resend caches a metrics answer for up to 15 minutes.
 */
export const EMAIL_CAMPAIGN_CAPTURE_DELAY_MINUTES = 20;

/** A checkpoint that failed this many times is no longer retried. */
export const EMAIL_CAMPAIGN_MAX_CHECKPOINT_ATTEMPTS = 48;

/**
 * A broadcast sent to fewer people than this is a test send to colleagues,
 * not a newsletter to the list. One threshold for both pages that tell them
 * apart: the tracker counts who it was SENT to (so a send is classed before
 * its deliveries come in); `/email-templates`, which reads only delivery
 * reports, counts who it was DELIVERED to. The two agree once a send has
 * been delivered. (Every test broadcast to date went to 1–3 colleagues.)
 */
export const NEWSLETTER_TEST_SEND_BELOW = 10;

/**
 * Resend's own unsubscribe link, as a campaign's `clicked_links` carries it.
 * Resend renders that link once PER READER (`https://unsubscribe.resend.com/
 * ?token=…`, the token a signed credential naming the reader's Resend
 * contact), so its clicked-links list has one row per reader who clicked it:
 * on 2026-10-17, 220 of the 295 rows across the mirrored broadcasts. The
 * tracker merges them into one row with this URL before it stores anything,
 * and the page shows that row as words, not as a link.
 */
export const EMAIL_CAMPAIGN_UNSUBSCRIBE_LINK_URL = "https://unsubscribe.resend.com/";

/**
 * Where Clavis's own unsubscribe links point (the email newsletter's): the
 * dashboard's public route, on the host its 301 to app.useclavis.ai leaves
 * alone, so links already in inboxes keep working. Each reader's link is
 * their own (`?flow=…&member_id=…&token=…&c=…`), so the tracker stores their
 * clicks as this one row, counts added up, as it does Resend's above.
 * `unsubscribe-tokens.ts` builds the links on it.
 */
export const CLAVIS_UNSUBSCRIBE_LINK_URL = "https://app.hercie.ai/unsubscribe";

/**
 * What a learning is (migration 232's CHECK): `observation` — what the
 * numbers showed; `conclusion` — what we now believe; `next_test` — the
 * experiment it proposes.
 */
export const EMAIL_CAMPAIGN_LEARNING_KINDS = ["observation", "conclusion", "next_test"] as const;
export type EmailCampaignLearningKind = (typeof EMAIL_CAMPAIGN_LEARNING_KINDS)[number];

export const EMAIL_CAMPAIGN_LEARNING_CONFIDENCES = ["low", "medium", "high"] as const;
export type EmailCampaignLearningConfidence = (typeof EMAIL_CAMPAIGN_LEARNING_CONFIDENCES)[number];

/**
 * An audience: a lower-case key with an optional `:goal` part (`trial`,
 * `winback:weight_loss`).
 */
export const EMAIL_CAMPAIGN_AUDIENCE_KEY_PATTERN = "^[a-z0-9_-]{1,40}(:[a-z0-9_-]{1,40})?$";

/** A learning's statement, trimmed (migration 232's CHECK). */
export const EMAIL_CAMPAIGN_STATEMENT_MAX = 2000;

/** A learning's `evidence` object, serialised as UTF-8. */
export const EMAIL_CAMPAIGN_EVIDENCE_MAX_BYTES = 8000;
